import { ref, watch } from "vue";
import { useSettings } from "../composables/use-settings";
import { Config } from "tf-api-client";

type DateInput = Date | string | number | undefined | null;

/** Locales the UI ships translations for (src/locales/*.json). */
export const SUPPORTED_UI_LOCALES = ["en", "de", "es", "fr", "it", "tr"];

/**
 * Maps a stored/browser locale (e.g. "en-GB") to one of the UI translation
 * locales, falling back to English. Settings written before this
 * normalization may contain region-qualified codes that have no matching
 * messages file.
 */
export function normalizeUiLocale(locale: string | undefined | null): string {
  const language = (locale || "").split("-")[0].toLowerCase();
  return SUPPORTED_UI_LOCALES.includes(language) ? language : "en";
}

class I18nUtil {
  private locale = ref<string>("en-GB");
  private timezone = ref<string>("UTC");

  private dateFormatter?: Intl.DateTimeFormat;
  private longDateFormatter?: Intl.DateTimeFormat;
  private dateTimeFormatter?: Intl.DateTimeFormat;

  // new: cache for number formatters keyed by options+locale
  private numberFormatters: Map<string, Intl.NumberFormat> = new Map();

  constructor() {
    const { settings, getSettingValue } = useSettings();

    const detectedLocale =
      typeof Intl !== "undefined" &&
      Intl.DateTimeFormat &&
      Intl.DateTimeFormat().resolvedOptions &&
      Intl.DateTimeFormat().resolvedOptions().locale
        ? Intl.DateTimeFormat().resolvedOptions().locale
        : "en-GB";

    const detectedTimeZone =
      typeof Intl !== "undefined" &&
      Intl.DateTimeFormat &&
      Intl.DateTimeFormat().resolvedOptions &&
      Intl.DateTimeFormat().resolvedOptions().timeZone
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : "UTC";

    this.locale.value = getSettingValue(Config.SettingKind.Locale, {
      default: detectedLocale,
    }) as string;
    this.timezone.value = getSettingValue(Config.SettingKind.Timezone, {
      default: detectedTimeZone,
    }) as string;

    this.resetFormatters();

    watch(
      settings,
      () => {
        this.locale.value = getSettingValue(Config.SettingKind.Locale, {
          default: detectedLocale,
        }) as string;
        this.timezone.value = getSettingValue(Config.SettingKind.Timezone, {
          default: detectedTimeZone,
        }) as string;
        this.resetFormatters();
      },
      { deep: true, immediate: true },
    );

    watch(
      [this.locale, this.timezone],
      () => {
        this.resetFormatters();
      },
      { immediate: false },
    );
  }

  private resetFormatters() {
    const tz = this.timezone.value || "UTC";
    const loc = this.locale.value || "en-GB";

    this.dateFormatter = new Intl.DateTimeFormat(loc, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone: tz,
    });

    this.longDateFormatter = new Intl.DateTimeFormat(loc, {
      dateStyle: "long",
      timeZone: tz,
    });

    this.dateTimeFormatter = new Intl.DateTimeFormat(loc, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZone: tz,
    });

    // clear cached number formatters when locale or related settings change
    this.numberFormatters.clear();
  }

  private toDate(input: DateInput): Date | null {
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    if (input == null) return null;
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  formatDate(input: DateInput): string {
    const date = this.toDate(input);
    if (!date || !this.dateFormatter) return "";
    return this.dateFormatter.format(date);
  }

  // e.g. "24 September 2026"
  formatLongDate(input: DateInput): string {
    const date = this.toDate(input);
    if (!date || !this.longDateFormatter) return "";
    return this.longDateFormatter.format(date);
  }

  formatDateTime(input: DateInput): string {
    const date = this.toDate(input);
    if (!date || !this.dateTimeFormatter) return "";
    return this.dateTimeFormatter.format(date);
  }

  private getNumberFormatter(
    options?: Intl.NumberFormatOptions,
  ): Intl.NumberFormat {
    const loc = this.locale.value || "en-GB";
    const key = `${loc}::${JSON.stringify(options || {})}`;
    let nf = this.numberFormatters.get(key);
    if (!nf) {
      nf = new Intl.NumberFormat(loc, options);
      this.numberFormatters.set(key, nf);
    }
    return nf;
  }

  formatNumber(
    value: number | string | undefined | null,
    options?: Intl.NumberFormatOptions,
  ): string {
    if (value == null || value === "") return "";
    const n = typeof value === "string" ? parseFloat(value) : value;
    if (!isFinite(n as number)) return "";
    const fmt = this.getNumberFormatter(options);
    return fmt.format(n as number);
  }

  formatCurrency(
    value: number | string | undefined | null,
    currency: string,
    options?: Intl.NumberFormatOptions,
  ): string {
    const opts: Intl.NumberFormatOptions = {
      style: "currency",
      currency,
      ...options,
    };
    return this.formatNumber(value, opts);
  }

  setLocale(locale: string) {
    this.locale.value = locale;
    this.resetFormatters();
  }

  setTimezone(timezone: string) {
    this.timezone.value = timezone;
    this.resetFormatters();
  }
}

const i18nUtil = new I18nUtil();
export default i18nUtil;
